import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Loader2, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, ago, errMsg, TASK_LABELS } from "@/lib/api";
import { Loading, PageHeader, StatusPill } from "@/components/common";

const toStr = (t) => (t ? `${t.provider}::${t.model_id}` : "");
const parse = (s) => { const [provider, ...rest] = s.split("::"); return provider && rest.length ? { provider, model_id: rest.join("::") } : null; };

function TargetInput({ value, onChange, testId }) {
  return <Input list="model-options" placeholder="provider::model-id" value={value} onChange={(e) => onChange(e.target.value)} className="font-mono text-xs" data-testid={testId} />;
}

function RouteCard({ r, benches, onSaved }) {
  const [primary, setPrimary] = useState(toStr(r.primary));
  const [fbs, setFbs] = useState((r.fallbacks || []).map(toStr));
  const [allow, setAllow] = useState(!!r.allow_fallback);
  const [busy, setBusy] = useState("");
  const save = async (enabled) => {
    setBusy("save");
    try {
      await api.put(`/admin/routes/${r.task}`, { primary: parse(primary), fallbacks: fbs.map(parse).filter(Boolean), allow_fallback: allow, enabled });
      toast.success(enabled ? "Route switched on" : "Route saved (off)");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const bench = async (s) => {
    const t = parse(s);
    if (!t) return toast.error("Choose a model as provider::model-id");
    setBusy(s);
    try { await api.post("/admin/benchmarks", { task: r.task, ...t }); toast.success("Benchmark started"); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const mine = benches.filter((b) => b.task === r.task).slice(0, 4);
  return (
    <section className="panel p-6" data-testid={`route-card-${r.task}`}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <p className="font-display text-lg font-semibold">{TASK_LABELS[r.task]}</p>
        <StatusPill status={r.enabled ? "active" : "disabled"} label={r.enabled ? "on" : "off"} testId={`route-status-${r.task}`} />
      </div>
      <div className="space-y-3">
        <div className="space-y-1"><Label className="text-xs">Primary model</Label>
          <div className="flex gap-2"><TargetInput value={primary} onChange={setPrimary} testId={`route-primary-${r.task}`} />
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => bench(primary)} data-testid={`route-bench-primary-${r.task}`} aria-label="Benchmark primary">
              {busy === primary ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}</Button></div></div>
        {fbs.map((f, i) => (
          <div key={i} className="space-y-1"><Label className="text-xs">Fallback {i + 1}</Label>
            <div className="flex gap-2"><TargetInput value={f} onChange={(v) => setFbs(fbs.map((x, k) => (k === i ? v : x)))} testId={`route-fallback-${r.task}-${i}`} />
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => bench(f)} aria-label="Benchmark fallback"><FlaskConical className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setFbs(fbs.filter((_, k) => k !== i))} aria-label="Remove fallback"><X className="h-4 w-4" /></Button></div></div>
        ))}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button size="sm" variant="ghost" onClick={() => setFbs([...fbs, ""])} data-testid={`route-add-fallback-${r.task}`}><Plus className="mr-1 h-3.5 w-3.5" />Add fallback</Button>
          <label className="flex items-center gap-2 text-xs">Use fallbacks <Switch checked={allow} onCheckedChange={setAllow} data-testid={`route-allow-fallback-${r.task}`} /></label>
        </div>
        {r.gates.length > 0 && <ul className="space-y-1 text-xs" data-testid={`route-gates-${r.task}`}>{r.gates.map((g, i) => (
          <li key={i} className="flex items-center gap-2"><StatusPill status={g.eligible ? "passed" : "blocked"} label={g.eligible ? "eligible" : "gated"} /><span className="truncate font-mono">{g.provider}/{g.model_id}</span>{!g.eligible && <span className="text-muted-foreground">· {g.reason}</span>}</li>))}</ul>}
        <div className="flex gap-2 pt-1">
          <Button size="sm" disabled={!!busy || !primary} onClick={() => save(true)} data-testid={`route-enable-${r.task}`}>{busy === "save" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}Save & switch on</Button>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => save(false)} data-testid={`route-disable-${r.task}`}>Save as off</Button>
        </div>
        {mine.length > 0 && (
          <div className="border-t pt-3" data-testid={`route-benchmarks-${r.task}`}><p className="eyebrow mb-2">Recent benchmarks</p>
            {mine.map((b) => (
              <div key={b.id} className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <StatusPill status={b.status === "running" ? "running" : b.passed ? "passed" : "failed"} label={b.status === "running" ? "running" : b.passed ? "passed" : "failed"} />
                <span className="font-mono">{b.provider}/{b.model_id}</span>
                {b.status === "completed" && <span className="text-muted-foreground">acc {b.accuracy} · quality {b.quality} · fail {b.failure_rate} · p50 {b.latency_ms_p50 ?? "—"}ms · ${b.est_cost_usd}</span>}
                <span className="text-muted-foreground">{ago(b.started_at)}</span>
              </div>))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function AdminRoutes() {
  const [routes, setRoutes] = useState(null);
  const [benches, setBenches] = useState([]);
  const [options, setOptions] = useState([]);
  const load = async () => {
    try {
      const [r, b] = await Promise.all([api.get("/admin/routes"), api.get("/admin/benchmarks")]);
      setRoutes(r.data); setBenches(b.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => {
    load();
    api.get("/admin/providers").then(async (r) => {
      const opts = [];
      for (const p of r.data.filter((x) => x.configured)) {
        const { data } = await api.get("/admin/models", { params: { provider: p.provider } });
        data.filter((m) => m.pricing_in != null && m.available !== false).forEach((m) => opts.push(`${p.provider}::${m.model_id}`));
      }
      setOptions(opts);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!benches.some((b) => b.status === "running")) return;
    const t = setInterval(() => api.get("/admin/benchmarks").then((b) => { setBenches(b.data); if (!b.data.some((x) => x.status === "running")) load(); }), 3000);
    return () => clearInterval(t);
  }, [benches]);
  return (
    <div>
      <PageHeader eyebrow="AI management" title="Models & benchmarks" testId="admin-routes-heading" />
      <p className="-mt-4 mb-6 max-w-3xl text-sm text-muted-foreground">A model can serve a task only after it passes that task's benchmark (accuracy ≥ 0.7, failure rate ≤ 0.34), has pricing set, and its provider is approved for personal data. Fallbacks must pass the same gates or the request stops safely. Benchmarks show quality on a fixed test set; they cannot guarantee correct output or ATS acceptance.</p>
      <datalist id="model-options">{options.map((o) => <option key={o} value={o} />)}</datalist>
      {!routes ? <Loading /> : <div className="grid gap-6 xl:grid-cols-2">{routes.map((r) => <RouteCard key={`${r.task}-${r.updated_at || ""}`} r={r} benches={benches} onSaved={load} />)}</div>}
    </div>
  );
}
